<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import { filter } from 'lodash-es';
import store from '../../../store';
import TwTitleBar from '../../../components/TwTitleBar';

export default {
  components: {
    TwTitleBar,
  },
  data() {
    return {
      filter: '',
      breadcrumbs: [
        { text: this.$t('menu.ski.title'), to: '', disabled: true },
        { text: this.$t('menu.ski.pages.areas.title'), to: { name: 'SkiAreas' } },
      ],
      headers: [
        { text: this.$t('skiAreas.dataHeaders.area'), value: 'name' },
        { text: this.$t('skiAreas.dataHeaders.country'), value: 'country.name' },
        { text: this.$t('skiAreas.dataHeaders.skiFacts'), value: 'meta.factsActive', align: 'right' },
        { text: this.$t('skiAreas.dataHeaders.snowGuarantee'), value: 'meta.snowGuaranteeActive', align: 'right' },
        { text: this.$t('skiAreas.dataHeaders.slopeMaps'), value: 'meta.slopeMapsActive', align: 'right' },
      ],
      pagination: {
        rowsPerPage: 10,
        descending: false,
        sortBy: 'name',
      },
      paginationLevels: [
        10,
        20,
        100,
        {
          text: '$vuetify.dataIterator.rowsPerPageAll',
          value: -1,
        },
      ],
      selectedCountry: null,
    };
  },
  computed: {
    ...mapState({
      areas: state => state.areas.areas.items,
      loading: state => state.areas.areas.loading,
      error: state => state.areas.areas.error,
    }),
    ...mapGetters({
      countries: 'app/skiCountries',
    }),
    filteredAreas() {
      if (!this.areas || !this.areas.length) {
        return [];
      }

      if (!this.selectedCountry) {
        return this.areas;
      }

      return filter(this.areas, area => area.country.id === this.selectedCountry);
    },
  },
  methods: {
    ...mapActions({
      fetchAreas: 'areas/fetchAreas',
      resetState: 'areas/resetState',
    }),
  },
  beforeRouteEnter(to, from, next) {
    store.dispatch('areas/fetchAreas')
      .then(() => {
        next();
      })
      .catch(() => {
        next();
      });
  },
};
</script>

<template>
  <v-content>
    <tw-title-bar
      :title="$t('menu.ski.pages.areas.title')"
      :breadcrumbs="breadcrumbs">
    </tw-title-bar>
    <v-container class="ski-areas">
      <v-layout row>
        <v-flex xs12>
          <v-container fluid>
            <v-layout row wrap>
              <v-flex grow class="pr-3">
                <v-text-field
                  v-model="filter"
                  clearable
                  solo
                  :label="$t('skiAreas.controls.search')"
                  append-icon="search"
                  browser-autocomplete="off"/>
              </v-flex>
              <v-flex xs4>
                <v-select
                  v-model="selectedCountry"
                  :items="countries"
                  :label="$t('skiAreas.controls.country')"
                  item-value="id"
                  item-text="name"
                  append-icon="public"
                  attach
                  clearable
                  solo/>
              </v-flex>
              <v-flex shrink>
                <v-btn
                  icon
                  :loading="loading"
                  @click="fetchAreas">
                  <v-icon>cached</v-icon>
                </v-btn>
              </v-flex>
            </v-layout>
            <v-card v-if="!error">
              <v-data-table
                :headers="headers"
                :items="filteredAreas || []"
                :search="filter"
                :rows-per-page-items="paginationLevels"
                :pagination.sync="pagination"
                :loading="loading"
                must-sort>
                <template v-slot:items="props">
                  <router-link
                    :to="{ name: 'SkiArea', params: { id: props.item.id } }"
                    tag="tr"
                    class="ski-areas__tr">
                    <td class="font-weight-medium">{{ props.item.name }}</td>
                    <td>{{ props.item.country.alias }}</td>
                    <td
                      :class="!props.item.meta.factsActive ?
                      'grey--text' : props.item.meta.factsInvalid ?
                      'red--text' : 'green--text'"
                      class="text-xs-right font-weight-medium">
                      {{ props.item.meta.factsActive }} / {{ props.item.meta.factsAvailable }}
                      <v-tooltip :disabled="!props.item.meta.factsInvalid" top>
                        <template v-slot:activator="{ on }">
                          <v-icon
                            :color="!props.item.meta.factsActive ?
                      'grey' : props.item.meta.factsInvalid ?
                      'red' : 'green'"
                            v-on="on"
                            class="ml-2"
                            small>
                            {{ props.item.meta.factsInvalid ? 'error' : 'check_circle' }}
                          </v-icon>
                        </template>
                        {{ $t('skiAreas.tooltips.factsInvalid')}}
                      </v-tooltip>
                    </td>
                    <td
                      :class="props.item.meta.snowGuaranteeActive ? 'green--text' : 'grey--text'"
                      class=" text-xs-right font-weight-medium">
                      {{ props.item.meta.snowGuaranteeActive ? 'Active' : 'Inactive'}}
                      <v-icon
                        :color="!props.item.meta.snowGuaranteeActive ?
                    'grey' : props.item.meta.snowGuaranteeInvalid ?
                    'red' : 'green'"
                        class="ml-2"
                        small>
                        {{ props.item.meta.snowGuaranteeInvalid ? 'error' : 'check_circle' }}
                      </v-icon>
                    </td>
                    <td
                      :class="props.item.meta.slopeMapsActive ? 'green--text' : 'grey--text'"
                      class=" text-xs-right font-weight-medium">
                      {{ props.item.meta.slopeMapsActive ? 'Active' : 'Inactive' }}
                      <v-icon
                        :color="props.item.meta.snowGuaranteeActive ? 'green' : 'grey'"
                        class="ml-2"
                        small>
                      </v-icon>
                    </td>
                  </router-link>
                </template>
              </v-data-table>
            </v-card>
            <div v-if="!loading && error" class="text-xs-center mt-5">
              {{ $t('skiAreas.error') }}
            </div>
          </v-container>
        </v-flex>
      </v-layout>
    </v-container>
  </v-content>
</template>

<style lang="scss">
  .ski-areas {
    &__tr {
      cursor: pointer;
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/ski/areas/SkiAreas.vue